import React from 'react';
import { Link } from 'react-router-dom';
import { getBrand } from '../utils/brandStyles';

/**
 * Shared page hero used on every service/info page.
 *
 * Props:
 *   market       – 'mwo' | 'bg' | 'lu'
 *   title        – H1 text
 *   subtitle     – body paragraph
 *   ctaText      – button label (optional)
 *   ctaLink      – path or URL (optional)
 *   ctaExternal  – true if ctaLink is an external URL
 *   icon         – React icon element (optional, shown only for Mind IT)
 */
const PageHero = ({ market, title, subtitle, ctaText, ctaLink, ctaExternal, icon }) => {
  const b = getBrand(market);

  const CTA = ctaText && ctaLink
    ? ctaExternal
      ? <a href={ctaLink} target="_blank" rel="noopener noreferrer" className={b.ctaBtnClass}>{ctaText}</a>
      : <Link to={ctaLink} className={b.ctaBtnClass}>{ctaText}</Link>
    : null;

  if (b.isMWO) {
    return (
      <section style={{
        background: '#0D0F14',
        padding: b.heroPad,
        position: 'relative',
        overflow: 'hidden',
        borderBottom: '1px solid rgba(255,255,255,0.06)'
      }}>
        {/* Dot grid atmosphere */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(0,188,212,0.055) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%)',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%)'
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
          {/* Cyan rule */}
          <div className="anim-slide-r" style={{ width: '48px', height: '3px', background: '#00BCD4', marginBottom: '40px' }} />

          <h1 className="anim-fade-up" style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(2.4rem, 6.5vw, 6rem)',
            lineHeight: 1.02,
            letterSpacing: '-0.02em',
            color: '#E8E4DC',
            maxWidth: '900px',
            marginBottom: '24px'
          }}>{title}</h1>

          {subtitle && (
            <p className="anim-fade-up delay-1" style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: 'clamp(1rem, 1.8vw, 1.15rem)',
              lineHeight: 1.75,
              color: 'rgba(232,228,220,0.78)',
              maxWidth: '560px',
              marginBottom: CTA ? '44px' : '0'
            }}>{subtitle}</p>
          )}

          {CTA && <div className="anim-fade-up delay-2">{CTA}</div>}
        </div>
      </section>
    );
  }

  /* ── Mind IT Hero ── */
  return (
    <section style={{
      background: b.heroBg,
      padding: b.heroPad,
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative glow */}
      <div style={{
        position: 'absolute', top: '-60px', right: '-60px',
        width: '400px', height: '400px', pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(0,188,212,0.07) 0%, transparent 68%)'
      }} />

      <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative' }}>
        {icon && (
          <div className="anim-fade-in" style={{ fontSize: '4rem', marginBottom: '20px', color: '#1A237E' }}>
            {icon}
          </div>
        )}

        <h1 className="anim-fade-up" style={{
          fontFamily: "'Libre Baskerville', Georgia, serif",
          fontWeight: 700,
          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
          lineHeight: 1.2,
          color: '#0F1B45',
          marginBottom: '20px',
          letterSpacing: '-0.02em'
        }}>{title}</h1>

        {subtitle && (
          <p className="anim-fade-up delay-1" style={{
            fontSize: 'clamp(1rem, 2vw, 1.15rem)',
            lineHeight: 1.75,
            color: '#4a5568',
            marginBottom: CTA ? '36px' : '0',
            maxWidth: '600px',
            margin: CTA ? '0 auto 36px' : '0 auto'
          }}>{subtitle}</p>
        )}

        {CTA && <div className="anim-fade-up delay-2">{CTA}</div>}
      </div>
    </section>
  );
};

export default PageHero;
